import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosClient, useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import EmployeeExitModal from '../components/EmployeeExitModal';
import { Button, StatusPill, Table, EmptyState, Modal, Input, Select, useToast } from '../../design-system';
import { nextPlanUp, planName } from '../../lib/plans';
import '../styles/OrgAdminUsers.css';

// Requesting a plan commits the firm to it, so it is the Admin's call — the
// server enforces the same rule in subscriptionController#requestSubscription.
const ADMIN_LEVEL = 5;

const emptyForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  roleName: '',
  isActive: true,
  // The employment record — what makes the account an employee rather than
  // just a login. `code` is assigned by the server and never sent back up.
  designation: '',
  department: '',
  employmentType: 'full_time',
  joiningDate: '',
  confirmationDate: '',
  reportingManagerId: '',
};

// Mirrors the enum on the auth-service User model. A CA firm's staff is mostly
// NOT permanent employees — articled assistants on a three-year training
// contract are the bulk of it.
const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'article', label: 'Articled assistant' },
  { value: 'intern', label: 'Intern' },
  { value: 'consultant', label: 'Consultant' },
];

const STATUS_VARIANT = {
  active: 'success',
  probation: 'info',
  serving_notice: 'warning',
  exited: 'neutral',
  inactive: 'neutral',
  removed: 'danger',
};

const STATUS_LABEL = {
  active: 'Active',
  probation: 'Probation',
  serving_notice: 'Serving notice',
  exited: 'Exited',
  inactive: 'Inactive',
  removed: 'Removed',
};

// The API takes dates as ISO strings and returns them as timestamps; <input
// type="date"> wants YYYY-MM-DD. Converting in one place stops the value being
// silently cleared on a save that never touched the field.
const dateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');

function OrgAdminUsers() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const myLevel = me?.role?.level ?? 0;
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // null | 'add' | 'edit'
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [exitingUser, setExitingUser] = useState(null);
  // The plan's seat position. Counted server-side from the live users (see
  // auth-service utils/subscription.js#seatUsage) rather than from `users.length`
  // here, so this figure and the one the server enforces on Add User can never
  // disagree — deactivated logins do not consume a seat, and this list shows them.
  const [seats, setSeats] = useState(null);
  // The plan's standing, including any request already awaiting a decision.
  // ⚠️ Needed as well as the seat count: without it this page offers "Request a
  // larger plan" to somebody who asked yesterday, the server refuses the second
  // one as a duplicate, and the screen looks broken. The Subscription panel
  // reads the same endpoint, so the two can't tell different stories.
  const [subscription, setSubscription] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
    axiosClient.get('/roles').then((res) => setRoles(res.data)).catch(() => {});
    axiosClient
      .get('/subscription')
      .then((res) => { setSeats(res.data.seats); setSubscription(res.data.subscription); })
      .catch(() => {});
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/users');
      setUsers(response.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setFormError('');
    setEditingUserId(null);
    setModalMode('add');
  };

  const openEditModal = (user) => {
    const emp = user.employment || {};
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      phone: user.phone || '',
      roleName: user.role?.name || '',
      isActive: user.isActive,
      designation: emp.designation || '',
      department: emp.department || '',
      employmentType: emp.type || 'full_time',
      joiningDate: dateInput(emp.joiningDate),
      confirmationDate: dateInput(emp.confirmationDate),
      reportingManagerId: emp.reportingManagerId || '',
    });
    setFormError('');
    setEditingUserId(user._id);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingUserId(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim() || !form.roleName) {
      setFormError('Name and role are required');
      return;
    }

    // Sent on both create and edit. Empty strings are passed through rather
    // than stripped: '' is how the server is told to CLEAR a date, so dropping
    // them would make a field impossible to un-set once filled.
    const employment = {
      designation: form.designation,
      department: form.department,
      type: form.employmentType,
      joiningDate: form.joiningDate || null,
      confirmationDate: form.confirmationDate || null,
      reportingManagerId: form.reportingManagerId || null,
    };

    setSaving(true);
    try {
      if (modalMode === 'add') {
        if (!form.email.trim() || !form.password) {
          setFormError('Email and password are required');
          setSaving(false);
          return;
        }
        await axiosClient.post('/users', {
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone || undefined,
          roleName: form.roleName,
          employment,
        });
        toast.success('User created');
      } else {
        await axiosClient.put(`/users/${editingUserId}`, {
          name: form.name,
          phone: form.phone || undefined,
          roleName: form.roleName,
          isActive: form.isActive,
          employment,
        });
        toast.success('User updated');
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // Removal is a soft delete server-side — the row survives because Attendance,
  // leave, timelogs and every audit entry point at it. What the user loses is
  // the login and their place in the list.
  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}? Their login stops working immediately. Their attendance and audit history is kept.`)) return;
    try {
      await axiosClient.delete(`/users/${u._id}`);
      toast.success(`${u.name} removed`);
      fetchUsers();
    } catch (err) {
      const data = err.response?.data;
      // The server refuses while a recorded exit has no handover — offer the
      // handover rather than just reporting the refusal.
      if (data?.code === 'HANDOVER_PENDING') {
        toast.danger('Hand their work over first');
        setExitingUser(u);
        return;
      }
      toast.danger(data?.error || 'Failed to remove user');
    }
  };

  // Inline role change straight from the table. Mirrors the server rules:
  // never your own row, never a user above you, never a role above you —
  // those render as a plain pill / disabled options instead of failing on save.
  const handleRoleChange = async (u, roleName) => {
    if (!roleName || roleName === u.role?.name) return;
    setRoleSavingId(u._id);
    try {
      const res = await axiosClient.put(`/users/${u._id}`, { roleName });
      setUsers((prev) => prev.map((x) => (x._id === u._id ? res.data : x)));
      toast.success(`${u.name} is now ${roleName}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to change role');
    } finally {
      setRoleSavingId(null);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (u) => (
        <button className="action-link user-name-link" onClick={() => navigate(`/org-admin/users/${u._id}`)}>
          {u.name}
        </button>
      ),
    },
    { key: 'email', header: 'Email', render: (u) => u.email },
    {
      key: 'role',
      header: 'Role',
      render: (u) => {
        const isSelf = u._id === me?._id;
        const isAbove = (u.role?.level ?? 0) > myLevel;
        if (isSelf || isAbove || roles.length === 0) {
          return <StatusPill variant="neutral" label={u.role?.name} />;
        }
        return (
          <select
            className="role-inline-select"
            value={u.role?.name || ''}
            disabled={roleSavingId === u._id}
            onChange={(e) => handleRoleChange(u, e.target.value)}
            aria-label={`Role for ${u.name}`}
          >
            {roles.map((r) => (
              <option key={r._id} value={r.name} disabled={r.level > myLevel}>
                {r.name}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: 'employment',
      header: 'Designation',
      render: (u) => (
        <span className="user-employment">
          {u.employment?.designation || '—'}
          {u.employment?.code && <em className="user-emp-code">{u.employment.code}</em>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      // employmentStatus is derived server-side, so probation and notice period
      // show here without this screen re-deriving them and disagreeing.
      render: (u) => {
        const status = u.employmentStatus || (u.isActive ? 'active' : 'inactive');
        return <StatusPill variant={STATUS_VARIANT[status] || 'neutral'} label={STATUS_LABEL[status] || status} />;
      },
    },
    {
      key: 'joined',
      header: 'Joined',
      // The employment joining date if the firm recorded one, else the day the
      // account was made — which is what this column silently meant before.
      render: (u) => new Date(u.employment?.joiningDate || u.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      render: (u) => {
        const isSelf = u._id === me?._id;
        const isAbove = (u.role?.level ?? 0) > myLevel;
        return (
          <div className="row-actions">
            <button className="action-link" onClick={() => openEditModal(u)}>Edit</button>
            {/* Offboarding moves client ownership, so it mirrors the server:
                never your own row, never someone above you. */}
            {!isSelf && !isAbove && myLevel >= 3 && (
              <>
                <button className="action-link" onClick={() => setExitingUser(u)}>
                  {u.employment?.exitDate ? 'Handover' : 'Offboard'}
                </button>
                <button className="action-link delete" onClick={() => removeUser(u)}>Remove</button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  // Who may ask, and for what. Mirrors the Subscription panel and the server's
  // own rule (requesting commits the firm, so it is the Admin's call): no button
  // while a request is open, and none on the largest plan, where there is
  // nothing above to ask for.
  const openRequest = subscription?.openRequest || null;
  const upgradePlan = nextPlanUp(subscription?.plan);
  const canRequestPlan = myLevel >= ADMIN_LEVEL && !openRequest && !!upgradePlan;

  return (
    <OrgAdminLayout title="Organization Users" subtitle="Manage your organization members">
      <div className="users-page">
        <div className="users-toolbar">
          <span className="users-count">
            {users.length} users
            {/* ⚠️ `unlimited` rather than a falsy check on the limit: the Large
                plan's cap is null, and `!limit` would render the biggest plan
                as the most restrictive. */}
            {seats && !seats.unlimited && (
              <span className={`users-seats${seats.isFull ? ' is-full' : ''}`}>
                {' · '}{seats.used} of {seats.limit} seats used
              </span>
            )}
          </span>
          <Button
            variant="primary"
            onClick={openAddModal}
            /* Disabled once the plan is full, with the reason in the tooltip.
               The server refuses it anyway (409) — this saves filling in a form
               that cannot be submitted. */
            disabled={!!seats && seats.isFull}
            title={seats && seats.isFull
              ? `Your plan allows ${seats.limit} users. Upgrade the subscription or deactivate someone who has left.`
              : undefined}
          >
            Add User
          </Button>
        </div>
        {/* This is where the ceiling actually bites — Add User is disabled and
            the person is standing here wanting a sixth user. Telling them to
            "ask for a larger plan" without a way to do it sends them off to
            hunt through Settings for a screen they have never opened. */}
        {seats && seats.isFull && (
          <div className="users-seat-warning">
            {/* A request already in flight changes what there is to say AND
                what there is to do. Offering the button again would send them
                to a form the server refuses as a duplicate — so the pending ask
                replaces it, exactly as it does on the Subscription panel. */}
            {openRequest ? (
              <span>
                You have used all {seats.limit} seats on your plan. The{' '}
                <strong>{planName(openRequest.plan)}</strong> plan was requested on{' '}
                {new Date(openRequest.requestedAt).toLocaleDateString()} and is awaiting approval —
                until it is decided, deactivating someone who has left is the way to free a seat.
              </span>
            ) : (
              <>
                <span>
                  You have used all {seats.limit} seats on your plan. To add someone,
                  deactivate a user who has left — or ask for a larger plan.
                </span>
                {canRequestPlan && (
                  <Button variant="primary" size="sm" onClick={() => navigate('/org-admin/settings/subscription')}>
                    {upgradePlan ? `Request the ${upgradePlan.name} plan` : 'Request a larger plan'}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {!loading && users.length === 0 ? (
          <EmptyState icon="👥" title="No users in your organization" />
        ) : (
          <Table columns={columns} data={users} loading={loading} />
        )}
      </div>

      <Modal
        isOpen={modalMode !== null}
        onClose={closeModal}
        title={modalMode === 'add' ? 'Add User' : 'Edit User'}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {modalMode === 'add' ? 'Create User' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <form className="user-form" onSubmit={handleSave}>
          {formError && <div className="form-error" role="alert">{formError}</div>}

          <Input
            label="Name *"
            id="name"
            name="name"
            value={form.name}
            onChange={handleFormChange}
            disabled={saving}
          />

          <Input
            label="Email *"
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleFormChange}
            disabled={saving || modalMode === 'edit'}
          />

          {modalMode === 'add' && (
            <Input
              label="Password *"
              id="password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleFormChange}
              disabled={saving}
            />
          )}

          <Input
            label="Phone"
            id="phone"
            name="phone"
            value={form.phone}
            onChange={handleFormChange}
            disabled={saving}
          />

          <Select
            label="Role *"
            id="roleName"
            name="roleName"
            value={form.roleName}
            onChange={handleFormChange}
            disabled={saving}
          >
            <option value="">-- Select role --</option>
            {roles.map((r) => (
              <option key={r._id} value={r.name}>{r.name}</option>
            ))}
          </Select>

          <div className="user-form-divider">Employment</div>

          <Input
            label="Designation"
            id="designation"
            name="designation"
            placeholder="e.g. Audit Manager"
            value={form.designation}
            onChange={handleFormChange}
            disabled={saving}
          />

          <Input
            label="Department"
            id="department"
            name="department"
            placeholder="e.g. Audit, GST, Income Tax"
            value={form.department}
            onChange={handleFormChange}
            disabled={saving}
          />

          <Select
            label="Employment type"
            id="employmentType"
            name="employmentType"
            value={form.employmentType}
            onChange={handleFormChange}
            disabled={saving}
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>

          <Input
            label="Joining date"
            id="joiningDate"
            name="joiningDate"
            type="date"
            value={form.joiningDate}
            onChange={handleFormChange}
            disabled={saving}
          />

          <Input
            label="Confirmation date"
            id="confirmationDate"
            name="confirmationDate"
            type="date"
            value={form.confirmationDate}
            onChange={handleFormChange}
            disabled={saving}
            helperText="Leave empty while they are on probation."
          />

          <Select
            label="Reports to"
            id="reportingManagerId"
            name="reportingManagerId"
            value={form.reportingManagerId}
            onChange={handleFormChange}
            disabled={saving}
          >
            <option value="">-- Nobody --</option>
            {users
              .filter((u) => u._id !== editingUserId && u.isActive !== false)
              .map((u) => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
          </Select>

          {modalMode === 'edit' && (
            <label className="user-active-checkbox">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleFormChange}
                disabled={saving}
              />
              Active
            </label>
          )}
        </form>
      </Modal>

      {exitingUser && (
        <EmployeeExitModal
          employee={exitingUser}
          colleagues={users}
          onClose={() => setExitingUser(null)}
          onChanged={fetchUsers}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminUsers;
